import {
  collection,
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

const COLLECTION = 'contact_submissions';

export type ContactSubmissionStatus = 'new' | 'read' | 'replied' | 'archived';

export interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  source: string;
  status: ContactSubmissionStatus;
  createdAt: Timestamp;
  repliedAt?: Timestamp;
  repliedBy?: string;
  adminNote?: string;
  ipAddress?: string | null;
  userAgent?: string | null;
}

export const contactSubmissionsService = {
  async getAll(): Promise<ContactSubmission[]> {
    const snap = await getDocs(
      query(collection(db, COLLECTION), orderBy('createdAt', 'desc'))
    );
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as ContactSubmission));
  },

  async getByStatus(status: ContactSubmissionStatus): Promise<ContactSubmission[]> {
    const snap = await getDocs(
      query(
        collection(db, COLLECTION),
        where('status', '==', status),
        orderBy('createdAt', 'desc')
      )
    );
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as ContactSubmission));
  },

  async markAsRead(id: string): Promise<void> {
    await updateDoc(doc(db, COLLECTION, id), { status: 'read' });
  },

  async markAsReplied(id: string, repliedBy: string): Promise<void> {
    await updateDoc(doc(db, COLLECTION, id), {
      status: 'replied',
      repliedAt: Timestamp.now(),
      repliedBy,
    });
  },

  async archive(id: string): Promise<void> {
    await updateDoc(doc(db, COLLECTION, id), { status: 'archived' });
  },

  async saveNote(id: string, note: string): Promise<void> {
    await updateDoc(doc(db, COLLECTION, id), { adminNote: note });
  },

  async delete(id: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, id));
  },

  formatDate(ts: Timestamp | undefined): string {
    if (!ts) return '—';
    return ts.toDate().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  },
};
